import { TDOId } from '@common-modules/universal/models/Brands';
import { IndividualRedactionCode } from '@redact-modules/redactionCodes/models';

export interface TranscriptionViewableWords {
  readonly id: string; // create
  readonly startTimeMs: number;
  readonly stopTimeMs: number;
  readonly words: string;
  readonly confidence: number; // 0 - 1
  readonly isSpacer?: boolean; // whether is a spacer
  // readonly forceVisible?: boolean // whether to force visible in transcription tab
}

export interface SpeakerTranscriptSegment {
  readonly speakerId: string;
  readonly speakerLabel: string;
  readonly startTimeMs: number;
  readonly stopTimeMs: number;
  readonly transcription: ReadonlyArray<TranscriptionViewableWords>;
}

export interface TranscriptionViewable {
  readonly id: string; // create
  /**
   * The engine that produced this transcript — which language it was run in,
   * since Redact picks a per-language transcription engine. Carried through so
   * callers can ask what EXISTS rather than what once ran: a completed task
   * survives deleting the transcript, so task history answers the wrong
   * question.
   */
  readonly engineId?: string;
  /**
   * The speaker-separation assets this view was built from, carried across the
   * worker boundary so deleting them needs no asset sweep (review B1/B2).
   */
  readonly speakerSeparationAssetIds?: ReadonlyArray<string>;
  readonly startOffsetMs: number;
  readonly stopOffsetMs: number;
  readonly transcription: ReadonlyArray<TranscriptionViewableWords>;
  readonly speakerSegments?: ReadonlyArray<SpeakerTranscriptSegment>;
}

/**
 * Once transcription words are part of an audio edit - add additional fields
 * These properties define redaction start and stop MS.
 */
export interface RedactedWords extends TranscriptionViewableWords {
  // TODO might want to rename RedactedWords to better name
  readonly coverage?: number;
  // add info about edit slice word is assigned to
  readonly editSliceMaxStartBuffer?: number | undefined; // largest stop buffer to allow
  readonly editSliceMaxStopBuffer?: number | undefined; // largest stop buffer to allow
  readonly editSliceStart: number;
  readonly editSliceStop: number;
  readonly editDetails: AudioEditDetails;
  // readonly audioEditSlice: AudioEditSlice; // TODO maybe sometday rewrite code to use this instead of editSliceStart, editSliceStop, editDetails
}

export interface TranscriptionViewState {
  // One or more selected word groups.
  readonly selected: ReadonlyArray<TranscriptionViewableWords>;
  // State of each word group.
  audioRedactionEdits: Array<AudioEditSlice>;
  voiceMaskEdits: Array<AudioEditSlice>;

  selectedAudioEditSlice?: AudioEditSlice;
  selectedAudioEditWord?: RedactedWords; // this is word that is right clicked when opening transcription menu
  editNotesOpen?: boolean;
  editBufferOpen?: boolean;
  currentEditWords?: string;
}

/**
 * [startMs, endMs, AudioEditDetails | null | undefined]
 */
export type AudioEditSlice = [
  number,
  number,
  AudioEditDetails | null | undefined,
];

export enum AudioEditType {
  MASK_VOICE,
  REDACT,
}
export interface AudioEditDetails {
  readonly user: string;
  readonly modifiedDateTime: string;
  readonly labels: Array<string>;
  readonly notes: string;
  readonly redactionCode?: IndividualRedactionCode;
  readonly hasAuditEvent?: boolean; // 3/22/2023 transitioning how audio redaction are added to audit log
  readonly voiceMask?: boolean; // ideally this would be at the slice level - but require lots of code refactor
  readonly startBufferMs?: number; // slice start padding relative to first word with full overlap (already incorporated) // ideally this would be at the slice level - but would require lots of code refactor
  readonly stopBufferMs?: number; //  slice stop padding relative to last word with full overlap (already incorporated) // ideally this would be at the slice level - but would require lots of code refactor
  // readonly lockStartTimeMs?: boolean; // might need something like this to prevent realignment with word start with custom start time
  // readonly lockStopTimeMs?: boolean; // might need something like this to prevent realignment with word start with custom stop time
}

export interface Transcription {
  readonly tdoId: TDOId;
  readonly engineId: string;
  /**
   * The vtn-standard asset this result was read from.
   *
   * Carried so deleting a result needs no second lookup: re-deriving it by
   * sweeping the TDO's assets and matching `sourceEngineId` both missed assets
   * produced by a different engine and only ever saw the first page of them
   * (review B1/B2). Optional because older cached records predate the field.
   */
  readonly assetId?: string;
  /**
   * Every asset a COMBINED record was built from, where one view spans several
   * chunk records. Set by combineSpeakerSeparationRecords; absent on the raw
   * records, which carry `assetId` instead.
   */
  readonly assetIds?: ReadonlyArray<string>;
  readonly startOffsetMs: number;
  readonly stopOffsetMs: number;
  readonly jsondata: {
    readonly sourceEngineId: string;
    readonly modifiedDateTime: number;
    readonly series?: ReadonlyArray<TranscriptionSeries>; // Undefined due to https://veritone.atlassian.net/browse/VTN-50009
  };
}

export interface TranscriptionSeries {
  readonly startTimeMs: number;
  readonly stopTimeMs: number;
  readonly speakerId?: string;
  readonly object?: {
    readonly label?: string;
    readonly type?: string;
  };
  readonly words?: ReadonlyArray<{
    readonly word: string;
    readonly confidence: number;
    readonly bestPath: boolean;
  }>;
}

export const getSeriesSpeakerId = (
  series: TranscriptionSeries
): string | undefined => {
  const id = series.speakerId ?? series.object?.label;
  if (id === undefined || id === '' || id === '?') {
    return undefined;
  }
  return id;
};
